import React, { useState } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { analyzeCampaignsEvolution, analyzeCampaignsFromAPI } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';

/* ─────────────────────────────────────── */
/* Campaigns Evolution — port of campaigns_evolution.py */
/* ─────────────────────────────────────── */

// Color palette matching tracking2 status colors
const STATUS_STYLE = {
    Purple: { bg: '#9C27B0', color: '#fff' },
    White: { bg: '#E0E0E0', color: '#333' },
    Green: { bg: '#4CAF50', color: '#fff' },
    Red: { bg: '#F44336', color: '#fff' },
    Orange: { bg: '#FF9800', color: '#fff' },
};

const EMPTY_RESULT = {
    evolution: { full: [], filtered: [] },
    sankey: null,
    weeklyCounts: [],
};

const makeWeekDefault = (offsetWeeks) => {
    const end = new Date();
    end.setDate(end.getDate() - offsetWeeks * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return {
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
    };
};

function CampaignsEvolution() {
    const [files, setFiles] = useState({ w1: null, w2: null, w3: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(EMPTY_RESULT);
    const [statusMsg, setStatusMsg] = useState('');
    const [filterCampaign, setFilterCampaign] = useState('');
    const [filterKeyword, setFilterKeyword] = useState('');
    const [filterW1, setFilterW1] = useState('');
    const [filterW2, setFilterW2] = useState('');
    const [filterW3, setFilterW3] = useState('');

    // Dual-mode: 'file' or 'api'
    const [dataSource, setDataSource] = useState('file');

    // 3 independent week pickers
    const [week1, setWeek1] = useState(makeWeekDefault(2));
    const [week2, setWeek2] = useState(makeWeekDefault(1));
    const [week3, setWeek3] = useState(makeWeekDefault(0));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (dataSource === 'file' && (!files.w1 || !files.w2 || !files.w3)) {
            setError('Please upload all 3 weekly files');
            return;
        }
        if (dataSource === 'api') {
            const profileId = localStorage.getItem('selectedAccountValue');
            if (!profileId) {
                setError('Please select an Amazon Ads account in the header before using API mode.');
                return;
            }
        }

        const executeAnalysis = async () => {
            setLoading(true);
            setError('');
            setStatusMsg(dataSource === 'api' ? '🔄 Fetching from Amazon API — this may take a few minutes...' : '🔄 Processing file...');
            try {
                let res;
                if (dataSource === 'api') {
                    const profileId = localStorage.getItem('selectedAccountValue');
                    res = await analyzeCampaignsFromAPI(profileId, { week1, week2, week3 });
                } else {
                    res = await analyzeCampaignsEvolution(files.w1, files.w2, files.w3);
                }
                setResult(res?.data ?? EMPTY_RESULT);
                if (res?.data) {
                    alertify.success('Analysis generated successfully!');
                }
            } catch (err) {
                setError(err.message);
                setStatusMsg('');
            } finally {
                setLoading(false);
                setStatusMsg('');
            }
        };

        if (dataSource === 'api') {
            setStatusMsg('📡 Connecting to Amazon API — preparing 3-week analysis...');
            alertify.confirm(
                'Amazon API Fetch Required',
                `We will fetch 3 separate weekly reports from Amazon:\n• Week 1: ${week1.start} → ${week1.end}\n• Week 2: ${week2.start} → ${week2.end}\n• Week 3: ${week3.start} → ${week3.end}\n\nThis may take a few minutes. Continue?`,
                function () { executeAnalysis(); },
                function () {
                    alertify.error('Analysis canceled.');
                    setStatusMsg('');
                }
            ).set('labels', { ok: 'Yes, fetch from API', cancel: 'Cancel' });
        } else {
            executeAnalysis();
        }
    };

    const safeResult = result ?? EMPTY_RESULT;
    const evolution = safeResult.evolution ?? EMPTY_RESULT.evolution;
    const filteredEvolution = evolution.filtered ?? [];
    const fullEvolution = evolution.full ?? [];
    const weeklyCounts = safeResult.weeklyCounts ?? [];

    const applyFilters = (data) => {
        return data.filter(row => {
            const matchCampaign = (row.campaign || '').toLowerCase().includes(filterCampaign.toLowerCase());
            const matchKeyword = (row.keyword_text || '').toLowerCase().includes(filterKeyword.toLowerCase());
            const matchW1 = !filterW1 || row.w1 === filterW1;
            const matchW2 = !filterW2 || row.w2 === filterW2;
            const matchW3 = !filterW3 || row.w3 === filterW3;
            return matchCampaign && matchKeyword && matchW1 && matchW2 && matchW3;
        });
    };

    const finalFilteredEvolution = applyFilters(filteredEvolution);
    const finalFullEvolution = applyFilters(fullEvolution);

    const buildSankeyData = () => {
        if (!safeResult.sankey) return null;
        const { nodes, links } = safeResult.sankey;
        return [{
            type: 'sankey',
            orientation: 'h',
            arrangement: 'freeform',
            node: {
                pad: 20,
                thickness: 20,
                line: { color: 'black', width: 0.5 },
                label: nodes.labels,
                color: nodes.colors,
                hovertemplate: 'Node: %{label}<br>Value: %{value}<extra></extra>',
            },
            link: {
                source: links.sources,
                target: links.targets,
                value: links.values,
                color: links.sources.map(i => nodes.colors[i] || '#ccc'),
                hovertemplate: 'Source: %{source.label}<br>Target: %{target.label}<br>Value: %{value}<extra></extra>',
            },
        }];
    };

    const renderStatusBadge = (status) => {
        const style = STATUS_STYLE[status] || { bg: '#9E9E9E', color: '#fff' };
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: '4px',
                fontWeight: 600,
                fontSize: '0.8rem',
                backgroundColor: style.bg,
                color: style.color,
                border: status === 'White' ? '1px solid #bbb' : 'none',
            }}>
                {status || '—'}
            </span>
        );
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Campaigns Evolution</h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* Upload / API form */}
                <div className="card mb-4">
                    <div className="card-body">
                        {/* Mode Toggle */}
                        <div className="btn-group mb-3" role="group">
                            <button type="button" className={`btn ${dataSource === 'file' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('file')}>
                                <i className="bi bi-file-earmark-arrow-up me-1"></i> Upload Files
                            </button>
                            <button type="button" className={`btn ${dataSource === 'api' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('api')}>
                                <i className="bi bi-cloud-download me-1"></i> Fetch from Amazon API
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 align-items-end">
                                {dataSource === 'file' ? (
                                    <>
                                        {['w1', 'w2', 'w3'].map((key, i) => (
                                            <div className="col-md-3" key={key}>
                                                <label className="form-label">Week {i + 1}</label>
                                                <input type="file" className="form-control form-control-sm" accept=".csv,.xlsx,.xls" onChange={e => setFiles(prev => ({ ...prev, [key] : e.target.files[0] }))} required />
                                            </div>
                                        ))}
                                    </>
                                 ) : (
                                    /* 3 independent week pickers */
                                    <div className="w-100">
                                        <div className="alert alert-info py-1 px-2 mb-3 small">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Account: <strong>{localStorage.getItem('selectedAccountName') || 'Not selected'}</strong>
                                            <span className="ms-3 text-muted">— Select any 3 weeks to compare (not necessarily consecutive)</span>
                                        </div>
                                        <div className="row g-3">
                                            {[
                                                { label: 'Week 1', state: week1, setter: setWeek1, color: '#0d6efd' },
                                                { label: 'Week 2', state: week2, setter: setWeek2, color: '#6f42c1' },
                                                { label: 'Week 3', state: week3, setter: setWeek3, color: '#198754' },
                                            ].map(({ label, state, setter, color }) => (
                                                <div className="col-md-4" key={label}>
                                                    <div className="card border-0 shadow-sm">
                                                        <div className="card-header py-1 text-white" style={{ backgroundColor: color, fontSize: '0.85rem', fontWeight: 600 }}>
                                                            {label}
                                                        </div>
                                                        <div className="card-body py-2 px-3">
                                                            <div className="row g-2">
                                                                <div className="col-6">
                                                                    <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>Start</label>
                                                                    <input type="date" className="form-control form-control-sm"
                                                                        value={state.start}
                                                                        onChange={e => setter(prev => ({ ...prev, start: e.target.value }))}
                                                                        required />
                                                                </div>
                                                                <div className="col-6">
                                                                    <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>End</label>
                                                                    <input type="date" className="form-control form-control-sm"
                                                                        value={state.end}
                                                                        onChange={e => setter(prev => ({ ...prev, end: e.target.value }))}
                                                                        required />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="col-12 mt-3 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-5" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : 'Analyze'}
                                    </button>
                                </div>
                            </div>
                        </form>
                        {/* Status indicator */}
                        {(loading || statusMsg) && (
                            <div className="mt-2 d-flex align-items-center" style={{ minHeight: 24 }}>
                                {loading && <span className="spinner-border spinner-border-sm text-primary me-2" style={{ width: 14, height: 14, borderWidth: 2 }} />}
                                {statusMsg && <small className="text-muted" style={{ fontSize: '0.8rem' }}>{statusMsg}</small>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Weekly counts */}
                {weeklyCounts.length > 0 && (
                    <div className="row mb-4 g-3">
                        {weeklyCounts.map(w => (
                            <div className="col-md-4" key={w.week}>
                                <div className="card text-center bg-light">
                                    <div className="card-body py-2">
                                        <small className="text-muted">{w.week}</small>
                                        <h5 className="mb-0">{w.campaigns} campaigns</h5>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                
                {/* ── Colors Legend ── */}
                <div className="card mb-4 border-info">
                    <div className="card-header bg-info bg-opacity-10 py-2">
                        <h6 className="mb-0 text-info-emphasis"><i className="bi bi-info-circle me-2"></i>Status Colors Legend</h6>
                    </div>
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap gap-4">
                            <div className="d-flex align-items-center"><span className="badge border text-dark me-2" style={{backgroundColor: 'white', width: '20px', height: '20px'}}>&nbsp;</span><small><strong>White:</strong> No spend ($0).</small></div>
                            <div className="d-flex align-items-center"><span className="badge text-white me-2" style={{backgroundColor: '#6f42c1', width: '20px', height: '20px'}}>&nbsp;</span><small><strong>Purple:</strong> Spend &le; $15, no sales (Not enough data).</small></div>
                            <div className="d-flex align-items-center"><span className="badge bg-success text-white me-2" style={{width: '20px', height: '20px'}}>&nbsp;</span><small><strong>Green:</strong> Sales generated, ACOS &lt; 30% (Healthy).</small></div>
                            <div className="d-flex align-items-center"><span className="badge bg-warning text-dark me-2" style={{width: '20px', height: '20px'}}>&nbsp;</span><small><strong>Orange:</strong> Sales generated, ACOS 30% - 69.9% (Warning).</small></div>
                            <div className="d-flex align-items-center"><span className="badge bg-danger text-white me-2" style={{width: '20px', height: '20px'}}>&nbsp;</span><small><strong>Red:</strong> Spend &gt; $15 with 0 sales, OR ACOS &ge; 70% (Loss).</small></div>
                        </div>
                    </div>
                </div>

                {/* ── Filtered Campaigns (Purple/White — Not Reviewed) ── */}
                {filteredEvolution.length > 0 && (
                    <div className="card mb-4 border-warning">
                        <div className="card-header bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                Filtered Campaigns (Purple / White — Not Reviewed)
                            </h5>
                            <span className="badge bg-warning text-dark fs-6">
                                {filteredEvolution.length} campaigns
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Campaign</th>
                                            <th>Keyword / Target</th>
                                            <th className="text-center" style={{width: '100px'}}>W1</th>
                                            <th className="text-center" style={{width: '100px'}}>W2</th>
                                            <th className="text-center" style={{width: '100px'}}>W3</th>
                                        </tr>
                                    
                                        <tr>
                                            <th><input type="text" className="form-control form-control-sm" placeholder="Filter..." value={filterCampaign} onChange={e => setFilterCampaign(e.target.value)} /></th>
                                            <th><input type="text" className="form-control form-control-sm" placeholder="Filter..." value={filterKeyword} onChange={e => setFilterKeyword(e.target.value)} /></th>
                                            <th>
                                                <select className="form-select form-select-sm" value={filterW1} onChange={e => setFilterW1(e.target.value)}>
                                                    <option value="">All</option>
                                                    {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </th>
                                            <th>
                                                <select className="form-select form-select-sm" value={filterW2} onChange={e => setFilterW2(e.target.value)}>
                                                    <option value="">All</option>
                                                    {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </th>
                                            <th>
                                                <select className="form-select form-select-sm" value={filterW3} onChange={e => setFilterW3(e.target.value)}>
                                                    <option value="">All</option>
                                                    {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {finalFilteredEvolution.map((row, i) => (
                                            <tr key={i}>
                                                <td className="fw-semibold text-truncate" style={{ maxWidth: 300 }}>{row.campaign}</td>
                                                <td className="text-truncate" style={{ maxWidth: 200 }}>{row.keyword_text || '—'}</td>
                                                <td className="text-center">{renderStatusBadge(row.w1)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w2)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w3)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sankey diagram */}
                {safeResult.sankey && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Campaign Status Transitions</h5>
                            <Plot
                                data={buildSankeyData()}
                                layout={{
                                    title: 'Status Flow: Week 1 → Week 2 → Week 3',
                                    height: 500,
                                    margin: { l: 20, r: 20, t: 50, b: 20 },
                                    font: { size: 12 },
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* Warning when no campaigns exist in the intersection */}
                {safeResult.weeklyCounts && fullEvolution.length === 0 && filteredEvolution.length === 0 && (
                    <div className="alert alert-info text-center border-0 shadow-sm mb-4 py-3" style={{ borderRadius: 10, background: 'rgba(13, 110, 253, 0.08)' }}>
                        <i className="bi bi-info-circle-fill text-primary me-2 fs-5"></i>
                        <span className="fw-semibold">No campaigns matched all 3 selected weeks:</span>
                        <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.875rem' }}>
                            The evolution table is an <strong>Inner Join</strong> (it only lists campaigns that were active in all three selected periods). Since you chose non-consecutive weeks (April 23, May 8, May 22), no campaign was active across all three.
                            <br />
                            <strong>Tip:</strong> Try selecting 3 consecutive weeks to see the full table!
                        </p>
                    </div>
                )}

                {/* Full Evolution table */}
                {fullEvolution.length > 0 && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Full Campaign Evolution Table</h5>
                            <span className="badge bg-secondary fs-6">
                                {fullEvolution.length} entries
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm table-striped mb-0">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Campaign</th>
                                            <th>Keyword / Target</th>
                                            <th className="text-center" style={{width: '100px'}}>W1</th>
                                            <th className="text-center" style={{width: '100px'}}>W2</th>
                                            <th className="text-center" style={{width: '100px'}}>W3</th>
                                        </tr>
                                    
                                        <tr>
                                            <th><input type="text" className="form-control form-control-sm" placeholder="Filter..." value={filterCampaign} onChange={e => setFilterCampaign(e.target.value)} /></th>
                                            <th><input type="text" className="form-control form-control-sm" placeholder="Filter..." value={filterKeyword} onChange={e => setFilterKeyword(e.target.value)} /></th>
                                            <th>
                                                <select className="form-select form-select-sm" value={filterW1} onChange={e => setFilterW1(e.target.value)}>
                                                    <option value="">All</option>
                                                    {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </th>
                                            <th>
                                                <select className="form-select form-select-sm" value={filterW2} onChange={e => setFilterW2(e.target.value)}>
                                                    <option value="">All</option>
                                                    {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </th>
                                            <th>
                                                <select className="form-select form-select-sm" value={filterW3} onChange={e => setFilterW3(e.target.value)}>
                                                    <option value="">All</option>
                                                    {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {finalFullEvolution.map((row, i) => (
                                            <tr key={i}>
                                                <td className="fw-semibold text-truncate" style={{ maxWidth: 300 }}>{row.campaign}</td>
                                                <td className="text-truncate" style={{ maxWidth: 200 }}>{row.keyword_text || '—'}</td>
                                                <td className="text-center">{renderStatusBadge(row.w1)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w2)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w3)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default CampaignsEvolution;
